import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Button,
  IconButton,
  Divider,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack,
  KeyboardDoubleArrowRight,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  KeyboardDoubleArrowLeft,
  Search,
  Refresh,
} from '@mui/icons-material';
import { beatMigrationService, Salesrep, Beat } from './beatMigrationService';
import { useSelector } from 'react-redux';
import { usePortalTranslation } from '../../providers/TranslationProvider';
import { getMetaDataConfig } from '../../services/uiConfigService';
import { GenericPopUp } from '../../components/popup/genericPopUp';
import { popupType } from '../../types/types';

const BeatMigration: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = usePortalTranslation();
  const [fromSalesrep, setFromSalesrep] = useState<Salesrep | null>(null);
  const [toSalesrep, setToSalesrep] = useState<Salesrep | null>(null);
  const [salesrepOptions, setSalesrepOptions] = useState<Salesrep[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [beatLoading, setBeatLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>('');
  const [availableBeats, setAvailableBeats] = useState<Beat[]>([]);
  const [selectedBeats, setSelectedBeats] = useState<Beat[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<Beat[]>([]);
  const [selectedForMigration, setSelectedForMigration] = useState<Beat[]>([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchSelected, setSearchSelected] = useState('');
  
  // Popup state for notifications
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<string>('');
  const [popupTypeState, setPopupTypeState] = useState<popupType>('Alert');

  // Handler to show popup
  const openPopup = (message: string, type: popupType) => {
    setPopupMessage(message);
    setPopupTypeState(type);
    setOpenGenericModal(true);
  };



  // Fetch ALL salesreps from API (auto-loads all data)
  const fetchSalesreps = async () => {
    try {
      setLoading(true);
      setApiError('');
      const getConfig = async () => {
        const config = await getMetaDataConfig("clientconfig","portal_configuration");
        return config;
      };
      const config = await getConfig();
      const userDesignationApi = config?.domainValues?.find((item: { name: string; }) => item.name === "userDesignationForBeat")?.value || "users?filter=designation[in]:[salesrep]&size=5000";
      console.log("userDesignation",userDesignationApi);
      
      // Fetch all salesreps automatically (no manual pagination needed)
      const salesreps = await beatMigrationService.fetchAllSalesreps(userDesignationApi ?? "");
      setSalesrepOptions(salesreps);
      
      if (salesreps.length === 0) {
        openPopup('No salesreps found.', 'Alert');
      }
    } catch (error: any) {
      console.error('Error fetching salesreps:', error);
      // openPopup(error.message || 'Failed to fetch salesreps. Please try again.', 'Alert');
      openPopup('Failed to fetch salesreps', 'Alert');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchSalesreps();
  }, []);

  // Fetch beats when From Salesrep is selected
  useEffect(() => {
    if (fromSalesrep) {
      fetchBeatsForSalesrep(fromSalesrep.loginId || fromSalesrep.id);
    } else {
      // Clear beats when no salesrep is selected
      setAvailableBeats([]);
      setSelectedBeats([]);
    }
  }, [fromSalesrep]);

  // Fetch ALL beats for selected salesrep (auto-loads all data)
  const fetchBeatsForSalesrep = async (salesrepId: string) => {
    try {
      console.log('🔄 Component: Starting to fetch beats for salesrep:', salesrepId);
      setBeatLoading(true);
      setApiError('');
      
      // Fetch all beats automatically (no manual pagination needed)
      const beats = await beatMigrationService.fetchAllBeatsForSalesrep(salesrepId);
      console.log('📊 Component: Received beats:', beats);
      setAvailableBeats(beats);
      setSelectedBeats([]); // Clear selected beats when fetching new ones
      
    } catch (error: any) {
      console.error('❌ Component: Error fetching beats:', error);
      // openPopup(error.message || 'Failed to fetch beats. Please try again.', 'Alert');
      openPopup('Failed to fetch beats', 'Alert');
      setAvailableBeats([]);
      setSelectedBeats([]);
    } finally {
      setBeatLoading(false);
      console.log('✅ Component: Beat loading completed');
    }
  };

  // Get available options for To Salesrep dropdown (exclude From Salesrep selection)
  const getToSalesrepOptions = () => {
    if (!fromSalesrep) {
      return salesrepOptions; // Show all options if no From Salesrep selected
    }
    return salesrepOptions.filter(option => option.id !== fromSalesrep.id);
  };

  // Get available options for From Salesrep dropdown (exclude To Salesrep selection)
  const getFromSalesrepOptions = () => {
    if (!toSalesrep) {
      return salesrepOptions; // Show all options if no To Salesrep selected
    }
    return salesrepOptions.filter(option => option.id !== toSalesrep.id);
  };

  const handleMoveToSelected = (beats: Beat[]) => {
    setSelectedBeats([...selectedBeats, ...beats]);
    setAvailableBeats(availableBeats.filter(beat => !beats.find(b => b.id === beat.id)));
    setSelectedAvailable([]);
  };

  const handleMoveToAvailable = (beats: Beat[]) => {
    setAvailableBeats([...availableBeats, ...beats]);
    setSelectedBeats(selectedBeats.filter(beat => !beats.find(b => b.id === beat.id)));
    setSelectedForMigration([]);
  };

  const handleMoveAllToSelected = () => {
    handleMoveToSelected(availableBeats);
  };

  const handleMoveAllToAvailable = () => {
    handleMoveToAvailable(selectedBeats);
  };

  // Execute beat migration
  const handleExecuteMigration = async () => {
    if (!fromSalesrep || !toSalesrep || selectedBeats.length === 0) {
      console.log('⚠️ Component: Migration validation failed:', {
        fromSalesrep: !!fromSalesrep,
        toSalesrep: !!toSalesrep,
        selectedBeatsCount: selectedBeats.length
      });
      openPopup('Please select both salesreps and at least one beat to migrate.', 'Alert');
      return;
    }

    try {
      console.log('🚀 Component: Starting migration execution');
      setBeatLoading(true);
      setApiError('');

      const beatIds = selectedBeats.map(beat => beat.originalBeatId);
      console.log('📋 Component: Migration details:', {
        fromSalesrep: fromSalesrep.name,
        toSalesrep: toSalesrep.name,
        beatIds,
        selectedBeats
      });

      await beatMigrationService.executeBeatMigration(
        fromSalesrep.loginId || fromSalesrep.id,
        toSalesrep.loginId || toSalesrep.id,
        beatIds
      );

      // Show success message
      // openPopup('Migration request submitted successfully! The migration will be processed asynchronously.', 'Success');
      openPopup('Migration completed successfully!', 'Success');
      console.log('🎉 Component: Migration completed successfully');
      
      // Reset the form
      setFromSalesrep(null);
      setToSalesrep(null);
      setSelectedBeats([]);
      setAvailableBeats([]);

    } catch (error: any) {
      console.error('❌ Component: Error executing migration:', error);
      // openPopup(error.message || 'Failed to execute migration. Please try again.', 'Alert');
      openPopup(`Migration failed:[${error.message}]`, 'Alert');
    } finally {
      setBeatLoading(false);
      console.log('✅ Component: Migration execution completed');
    }
  };

  return (
    <Box sx={{ padding: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
        <IconButton sx={{ marginRight: 2 }} onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" component="h3" color="#333" fontWeight={500}>
          Beat Migration
        </Typography>
      </Box>

      <Card elevation={2}>
        <CardContent sx={{ padding: 4 }}>

        <Grid container spacing={3}>
          <Grid item xs={18} md={9}>
          {/* Salesrep Selection */}
          <Grid container spacing={3} sx={{ marginBottom: 4 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1"  sx={{ marginBottom: 1, borderLeft: 'none !important'  , fontWeight: "500 !important" }}>
                From Salesrep
              </Typography>
              <Autocomplete
                value={fromSalesrep}
                onChange={(event, newValue) => {
                  setFromSalesrep(newValue);
                  // Clear To Salesrep if it's the same as the newly selected From Salesrep
                  if (toSalesrep && newValue && toSalesrep.id === newValue.id) {
                    setToSalesrep(null);
                  }
                }}
                options={getFromSalesrepOptions()}
                loading={loading}
                getOptionLabel={(option) => `${option.name} (${option.code || option.loginId || option.id})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={loading ? "Loading..." : "Select Salesrep..."}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          height: '40px',
                          padding:"0px !important",
                          paddingLeft:"10px !important",
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#24c6b1',
                          },
                        },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" sx={{ marginBottom: 1 , borderLeft: 'none !important'  , fontWeight: "500 !important"  }}>
                To Salesrep
              </Typography>
              <Autocomplete
                value={toSalesrep}
                onChange={(event, newValue) => {
                  setToSalesrep(newValue);
                  // Clear From Salesrep if it's the same as the newly selected To Salesrep
                  if (fromSalesrep && newValue && fromSalesrep.id === newValue.id) {
                    setFromSalesrep(null);
                  }
                }}
                options={getToSalesrepOptions()}
                loading={loading}
                getOptionLabel={(option) => `${option.name} (${option.code || option.loginId || option.id})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={loading ? "Loading..." : "Select Salesrep..."}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          height: '40px',
                          padding:"0px !important",
                          paddingLeft:"10px !important",
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#24c6b1',
                          },
                        },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Beat Lists and Transfer Controls */}
          <Grid container spacing={3}>
            <Grid item xs={5.5} md={5.5}>
                      <Typography variant="h6" sx={{ marginBottom: 2, borderLeft: 'none !important', fontWeight: "500 !important" }}>
                        Available Beats
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Search available beats..."
                        value={searchAvailable}
                        onChange={(e) => setSearchAvailable(e.target.value)}
                        InputProps={{
                          startAdornment: <Search sx={{ color: '#9e9e9e', marginRight: 1 }} />,
                        }}
                        sx={{
                          marginBottom: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          height: '40px',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#24c6b1',
                          },
                        },
                        }}
                      />
                      <Paper
                        variant="outlined"
                        sx={{
                          height: 400,
                          overflow: 'auto',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <List sx={{ padding: 1 }}>
                          {beatLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                              <Typography variant="body2" color="#666">
                                Loading beats...
                              </Typography>
                            </Box>
                          ) : availableBeats.length === 0 && selectedBeats.length === 0 && fromSalesrep ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                              <Typography variant="body2" color="#666">
                                No beats found for this salesrep
                              </Typography>
                            </Box>
                          ) : availableBeats
                            .filter(beat => beat.name && beat.id) // Filter out beats with null/undefined name or id
                            .filter(beat => 
                              beat.name?.toLowerCase().includes(searchAvailable.toLowerCase()) ||
                              beat.originalBeatId?.toLowerCase().includes(searchAvailable.toLowerCase())
                            )
                            .map((beat) => (
                              <ListItem
                                key={beat.id}
                                disablePadding
                                sx={{
                                  margin: '4px 0',
                                  borderRadius: '6px',
                                  border: '1px solid #e0e0e0',
                                  backgroundColor: selectedAvailable.find(b => b.id === beat.id) ? '#e3f2fd' : 'white',
                                  fontWeight: "500 !important"
                                }}
                              >
                                <ListItemButton
                                  onClick={() => {
                                    const isSelected = selectedAvailable.find(b => b.id === beat.id);
                                    if (isSelected) {
                                      setSelectedAvailable(selectedAvailable.filter(b => b.id !== beat.id));
                                    } else {
                                      setSelectedAvailable([...selectedAvailable, beat]);
                                    }
                                  }}
                                  sx={{
                                    borderRadius: '6px',
                                    '&:hover': {
                                      backgroundColor: selectedAvailable.find(b => b.id === beat.id) ? '#e3f2fd' : '#f5f5f5',
                                    },
                                    fontWeight: "500 !important"
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography variant="body1" fontWeight="500" color="#333">
                                        {beat.name}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography variant="body2" color="#666">
                                        ({beat.originalBeatId})
                                      </Typography>
                                    }
                                  />
                                  <Chip
                                    label={beat.count}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#24c6b1',
                                      color: 'white',
                                      fontWeight: "500 !important",
                                      minWidth: '33px',
                                      height: '24px',
                                      borderRadius: '12px',

                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                        </List>
                      </Paper>
                    </Grid>

                    {/* Transfer Controls */}
                    <Grid item xs={1} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 400,
                          gap: 2,
                        }}
                      >
                        <IconButton
                          onClick={handleMoveAllToSelected}
                          disabled={availableBeats.length === 0}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: '#f5f5f5',
                            color: availableBeats.length > 0 ? '#333' : '#9e9e9e',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: 1,
                            '&:hover': {
                              backgroundColor: '#e0e0e0',
                            },
                            '&:disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#9e9e9e',
                              border: '1px solid #e0e0e0',
                            },
                          }}
                        >
                          <KeyboardDoubleArrowRight fontSize="medium" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleMoveToSelected(selectedAvailable)}
                          disabled={selectedAvailable.length === 0}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: '#f5f5f5',
                            color: selectedAvailable.length > 0 ? '#333' : '#9e9e9e',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: 1,
                            '&:hover': {
                              backgroundColor: '#e0e0e0',
                            },
                            '&:disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#9e9e9e',
                              border: '1px solid #e0e0e0',
                            },
                          }}
                        >
                          <KeyboardArrowRight fontSize="medium" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleMoveToAvailable(selectedForMigration)}
                          disabled={selectedForMigration.length === 0}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: '#f5f5f5',
                            color: selectedForMigration.length > 0 ? '#333' : '#9e9e9e',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: 1,
                            '&:hover': {
                              backgroundColor: '#e0e0e0',
                            },
                            '&:disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#9e9e9e',
                              border: '1px solid #e0e0e0',
                            },
                          }}
                        >
                          <KeyboardArrowLeft fontSize="medium" />
                        </IconButton>
                        <IconButton
                          onClick={handleMoveAllToAvailable}
                          disabled={selectedBeats.length === 0}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: '#f5f5f5',
                            color: selectedBeats.length > 0 ? '#333' : '#9e9e9e',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: 1,
                            '&:hover': {
                              backgroundColor: '#e0e0e0',
                            },
                            '&:disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#9e9e9e',
                              border: '1px solid #e0e0e0',
                            },
                          }}
                        >
                          <KeyboardDoubleArrowLeft fontSize="medium" />
                        </IconButton>
                      </Box>
                    </Grid>

                    {/* Selected Beats */}
                    <Grid item xs={5.5} md={5.5}>
                  {/* <Box sx={{ width: '100%', textAlign: 'right' }}> */}
                    <Typography
                      variant="h6"
                      sx={{
                        marginBottom: 2,
                        borderLeft: 'none !important',
                        fontWeight: '500 !important',
                        display: 'inline-block', // optional, keeps the Typography from stretching full width
                      }}
                    >
                      Selected Beats for Migration
                    </Typography>
                  {/* </Box> */}
                  <TextField
                        fullWidth
                        placeholder="Search selected beats..."
                        value={searchSelected}
                        onChange={(e) => setSearchSelected(e.target.value)}
                        InputProps={{
                          startAdornment: <Search sx={{ color: '#9e9e9e', marginRight: 1 }} />,
                        }}
                        sx={{
                          marginBottom: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          height: '40px',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#24c6b1',
                          },
                        },
                        }}
                      />
                      <Paper
                        variant="outlined"
                        sx={{
                          height: 400,
                          overflow: 'auto',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <List sx={{ padding: 1 }}>
                          {beatLoading ? 
                          (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                              {/* <Typography variant="body2" color="#666">
                                Loading beats...
                              </Typography> */}
                            </Box>
                          ) 
                          : 
                          selectedBeats
                            .filter(beat => beat.name && beat.id) // Filter out beats with null/undefined name or id
                            .filter(beat => 
                              beat.name?.toLowerCase().includes(searchSelected.toLowerCase()) ||
                              beat.originalBeatId?.toLowerCase().includes(searchSelected.toLowerCase())
                            )
                            .map((beat) => (
                              <ListItem
                                key={beat.id}
                                disablePadding
                                sx={{
                                  margin: '4px 0',
                                  borderRadius: '6px',
                                  border: '1px solid #e0e0e0',
                                  backgroundColor: selectedForMigration.find(b => b.id === beat.id) ? '#e3f2fd' : 'white',
                                }}
                              >
                                <ListItemButton
                                  onClick={() => {
                                    const isSelected = selectedForMigration.find(b => b.id === beat.id);
                                    if (isSelected) {
                                      setSelectedForMigration(selectedForMigration.filter(b => b.id !== beat.id));
                                    } else {
                                      setSelectedForMigration([...selectedForMigration, beat]);
                                    }
                                  }}
                                  sx={{
                                    borderRadius: '6px',
                                    '&:hover': {
                                      backgroundColor: selectedForMigration.find(b => b.id === beat.id) ? '#e3f2fd' : '#f5f5f5',
                                    },
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography variant="body1" fontWeight="500" color="#333">
                                        {beat.name}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography variant="body2" color="#666">
                                        ({beat.originalBeatId})
                                      </Typography>
                                    }
                                  />
                                  <Chip
                                    label={beat.count}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#24c6b1',
                                      color: 'white',
                                      fontWeight: '500',
                                      minWidth: '33px',
                                      height: '24px',
                                      borderRadius: '12px',
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                        </List>
                      </Paper>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              marginTop: 4,
            }}
          >
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              sx={{
                color: '#333',
                borderColor: '#e0e0e0',
                backgroundColor: 'white',
                borderRadius: '15px',
                padding: '12px 32px',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '16px',
                minWidth: '140px',
                '&:hover': {
                  borderColor: '#ccc',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={selectedBeats.length === 0 || !fromSalesrep || !toSalesrep || beatLoading}
              onClick={handleExecuteMigration}
              sx={{
                backgroundColor: '#24c6b1',
                borderRadius: '15px',
                padding: '12px 32px',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '16px',
                minWidth: '180px',
                '&:hover': {
                  backgroundColor: '#20b3a1',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                },
              }}
            >
              {beatLoading ? 'Loading...' : 'Execute Migration'}
            </Button>
          </Box>
          </Grid>

          {/* Right Section - Beat Migration Process */}
          <Grid item xs={12} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  padding: 3,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Heading */}
                  <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: "500", borderLeft: 'none !important', paddingLeft: '0 !important' }}>
                Beat Migration Process
              </Typography>
                  {/* Description */}
                  <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', lineHeight: 1.5 }}>
                    Beat migration allows you to transfer sales territories (beats) from one sales representative to another within the same state or across different territories.
                  </Typography>

                  {/* Steps */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '500', color: '#333', fontSize: '15px', marginBottom: 1 , borderLeft: 'none !important', paddingLeft: '0 !important' }}>
                      Steps:
                    </Typography>
                    
                    {/* Step 1 */}
                    {/* <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', lineHeight: 1.4 }}>
                       1. Select the state where the migration will occur
                      </Typography>
                    </Box> */}

                    {/* Step 2 */}
                    {/* <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', lineHeight: 1.4 }}>
                      2. Choose the beat creation date to filter relevant beats
                      </Typography>
                    </Box> */}

                    {/* Step 3 */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#333', lineHeight: 1.4 }}>
                        1. Choose the source salesrep (From Salesrep)
                      </Typography>
                    </Box>

                    {/* Step 4 */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color:  '#333', lineHeight: 1.4 }}>
                        2. Choose the destination salesrep (To Salesrep)
                      </Typography>
                    </Box>

                    {/* Step 5 */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#333', lineHeight: 1.4 }}>
                       3. Select beats to migrate using the arrow controls
                      </Typography>
                    </Box>

                    {/* Step 6 */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#333', lineHeight: 1.4 }}>
                        4. Execute the migration
                      </Typography>
                    </Box>
                  </Box>

                  {/* Date-based Beat Creation Note */}
                  {/* <Box sx={{ 
                    px: 0.5, 
                    py: 1, 
                    backgroundColor: '#dfeaf5', 
                    borderRadius: '6px', 
                    border: '1px solid #e9ecef',
                    alignItems: 'justify'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '500', marginBottom: 0.5, color: '#333', fontSize: '12px !important' , borderLeft: 'none !important' }}>
                      Date-based Beat Creation:  
                      <span style={{fontWeight: '400'}}> Beats are created on specific dates by clients. Use the date picker to filter and view only the beats created on the selected date.</span>
                    </Typography>
                   
                  </Box> */}

                  {/* Important Note */}
               
                    <Typography variant="subtitle2" sx={{ fontWeight: '500', marginBottom: 0.5, color: '#856404', fontSize: '13px !important' , borderLeft: 'none !important', paddingLeft: '0 !important' }}>
                      Important : <span style={{fontWeight: '400'}}>  All customers and outlets within the selected beats will be automatically transferred to the new salesrep. Historical data and performance metrics will be preserved.</span>
                    </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Generic Popup for success/error notifications */}
      <GenericPopUp
        message={popupMessage}
        type={popupTypeState}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
    </Box>
  );
};

export default BeatMigration;
